"use client";

import { memo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer } from "recharts";
import type { DetailedQuestionDataPoint } from "../types";
import { formatTime } from "../utils/formatUtils";
import { ACTIVITY_CHART_COLORS } from "../../../../ActivityChart/constants";
import { useTranslations } from "next-intl";

interface QuestionChartProps {
  data: DetailedQuestionDataPoint[];
  height?: number;
}

/**
 * Определяет цвет для вопроса на основе результата
 */
function getQuestionColor(point: DetailedQuestionDataPoint): string {
  if (point.result === 1 || point.result === 3) {
    return ACTIVITY_CHART_COLORS.normal;
  }
  return ACTIVITY_CHART_COLORS.exceeded;
}

/**
 * Кастомный Tooltip для графика вопросов
 */
interface QuestionTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: DetailedQuestionDataPoint }>;
  label?: string | number;
}

const QuestionTooltip = memo(function QuestionTooltip({
  active,
  payload,
  label,
}: QuestionTooltipProps) {
  const t = useTranslations()
  if (!active || !payload?.length) {
    return null;
  }

  const data = payload[0];
  const timeInSeconds = data.value;
  const point = data.payload;

  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md">
      <p className="text-sm font-medium">
        {t('label-question')} {point.questionPos}
      </p>
      <div className="flex items-center gap-2 mt-1">
        <span
          className="w-3 h-3 rounded-sm"
          style={{
            backgroundColor: getQuestionColor(point),
          }}
        />
        <span className="text-sm">
          <span className="font-semibold">{formatTime(timeInSeconds, {
            'label-seconds-short': t('label-seconds-short'),
            'label-minute': t('label-minute')
          })}</span>
        </span>
      </div>
    </div>
  );
});

/**
 * Компонент для отображения гистограммы вопросов
 * Показывает время ответа на каждый вопрос с цветовой кодировкой по правильности
 */
export const QuestionChart = memo(function QuestionChart({
  data,
  height = 150
}: QuestionChartProps) {
  const t = useTranslations();

  if (data.length === 0) {
    return null;
  }

  return (
    <div style={{ width: '100%', height, minHeight: height || 150 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={height || 150}>
        <BarChart
          data={data}
          maxBarSize={4}
          margin={{ top: 8, right: 8, left: 0, bottom: 18 }} // больше места снизу
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          {/* Убираем все надписи слева */}
          <YAxis
            tick={false}        // убирает цифры
            axisLine={false}    // убирает линию оси
            tickLine={false}    // убирает риски
            width={0}           // освобождает место
            label={undefined}   // на всякий случай
          />

          <XAxis
            dataKey="questionPos"
            tick={{ fontSize: 10 }}
            tickMargin={4}
            height={34} // даем место подписи
            label={{
              value: t('label-question-number').replace('№', '').trim(),
              position: "insideBottom",
              offset: -16,      // больше по модулю => ниже (для insideBottom)
              fontSize: 11,
            }}
          />

          <Tooltip
            cursor={{ stroke: "rgba(0,0,0,0.25)", strokeWidth: 1 }}
            content={<QuestionTooltip />}
          />

          <Bar dataKey="time" radius={[2, 2, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getQuestionColor(entry)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});